import { useEffect, useState, type ReactNode } from "react";
import { apiFetch } from "../utils/api";

// Порт из смежного проекта SNS CRM (frontend/src/components/helpMarkup.tsx), эпик #255 H2.
// MVP: рендер текста статьи (## подзаголовок, списки, **жирный**, `код`). Скриншоты
// (маркер shot:slot) и туры — отдельные задачи H8/H6: пока строка-картинка рисуется
// ненавязчивым плейсхолдером «скриншот ещё не снят».

export type HelpArticle = {
  id: number;
  slug: string;
  page_key: string | null;
  title: string;
  body: string;
  position: number;
  updated_at: string;
};

/** Ключ экрана для контекстной справки. В ANYskin экран = значение `view` (react-router
 *  нет), поэтому маппинг тривиален — ключ совпадает с view. Функция оставлена точкой
 *  расширения (напр. учесть вложенный activeTab на «Анализе»). */
export function pageKeyFor(view: string): string {
  return view;
}

/** Метаданные скриншота справки (H8, #263): слот → есть картинка. */
export type HelpImage = { slot: string; mime: string; width: number | null; height: number | null; caption: string | null; updated_at?: string };

/** Картинка слота по короткоживущей подписанной ссылке (#296: прежде GET /images/{slot}
 *  был публичным — угадываемое имя слота открывало доступ кому угодно; теперь ссылку
 *  выдаёт ТОЛЬКО авторизованный GET /images/{slot}/link, и живёт она 5 минут). Пока
 *  ссылка не получена — пустой плейсхолдер того же размера, чтобы макет не прыгал. */
export function HelpShotImg({ slot, alt, className }: { slot: string; alt: string; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setUrl(null);
    apiFetch<{ url: string }>(`/api/help/images/${encodeURIComponent(slot)}/link`)
      .then((r) => { if (alive) setUrl(r.url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slot]);
  if (!url) return <div className={`${className ?? ""} animate-pulse bg-slate-100`} aria-label={alt} />;
  return <img src={url} alt={alt} loading="lazy" className={className} />;
}

/** Разметка markdown-lite: ## подзаголовок, - список, 1. нумерация, **жирный**, `код`,
 *  плюс скриншоты `![подпись](shot:слот)`. Если для слота есть картинка (её слот в
 *  `images`) — рисуем `<HelpShotImg>` (подписанная ссылка, #296), иначе ненавязчивый
 *  плейсхолдер «скриншот ещё не снят» (H8: слот заведён, съёмка — задача H9). */
export function Markup({ text, images }: { text: string; images?: Set<string> }) {
  const inline = (s: string, key: string) =>
    s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean).map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**"))
        return <b key={`${key}-${i}`} className="font-semibold text-slate-900">{p.slice(2, -2)}</b>;
      if (p.startsWith("`") && p.endsWith("`"))
        return <code key={`${key}-${i}`} className="rounded bg-slate-100 px-1 py-0.5 text-[12px] font-mono">{p.slice(1, -1)}</code>;
      return <span key={`${key}-${i}`}>{p}</span>;
    });

  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => {
    if (!list) return;
    const { items, ordered } = list;
    blocks.push(
      ordered
        ? <ol key={`l${blocks.length}`} className="my-2 space-y-1 pl-5 list-decimal">{items.map((it, i) => <li key={i}>{inline(it, `o${i}`)}</li>)}</ol>
        : <ul key={`l${blocks.length}`} className="my-2 space-y-1 pl-5 list-disc">{items.map((it, i) => <li key={i}>{inline(it, `u${i}`)}</li>)}</ul>,
    );
    list = null;
  };

  text.replace(/\r/g, "").split("\n").forEach((raw, idx) => {
    const line = raw.trimEnd();
    const img = /^!\[(.*?)\]\((shot|img):([^)]+)\)$/.exec(line.trim());
    if (img) {
      flush();
      const slot = img[3].trim();
      const caption = img[1] || slot;
      if (images?.has(slot)) {
        blocks.push(
          <figure key={idx} className="my-3">
            <HelpShotImg slot={slot} alt={caption}
              className="rounded-lg border border-slate-200 shadow-sm max-w-full" />
            {img[1] && <figcaption className="mt-1 text-xs text-slate-400">{img[1]}</figcaption>}
          </figure>,
        );
      } else {
        blocks.push(
          <p key={idx} className="my-2 rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-400">
            скриншот «{caption}» ещё не снят
          </p>,
        );
      }
      return;
    }
    const h = /^#{2,3}\s+(.*)$/.exec(line);
    const ul = /^[-*]\s+(.*)$/.exec(line);
    const ol = /^\d+[.)]\s+(.*)$/.exec(line);
    if (h) { flush(); blocks.push(<h4 key={idx} className="mt-4 mb-1 font-bold text-slate-900 text-sm">{h[1]}</h4>); return; }
    if (ul) { if (!list || list.ordered) { flush(); list = { ordered: false, items: [] }; } list.items.push(ul[1]); return; }
    if (ol) { if (!list || !list.ordered) { flush(); list = { ordered: true, items: [] }; } list.items.push(ol[1]); return; }
    flush();
    if (line.trim()) blocks.push(<p key={idx} className="my-2 leading-relaxed">{inline(line, String(idx))}</p>);
  });
  flush();
  return <div className="text-slate-700 text-sm">{blocks}</div>;
}
